import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import client from '../../api/client';
import { formatPrice } from '../../utils/oddsFormat';

const SPORT_KEYS = [
  { key: 'americanfootball_nfl', label: 'NFL' }, { key: 'americanfootball_ncaaf', label: 'NCAAF' },
  { key: 'basketball_nba', label: 'NBA' }, { key: 'basketball_ncaab', label: 'NCAAB' },
  { key: 'baseball_mlb', label: 'MLB' }, { key: 'icehockey_nhl', label: 'NHL' }, { key: 'soccer_usa_mls', label: 'MLS' },
  { key: 'mma_mixed_martial_arts', label: 'MMA' }, { key: 'boxing_boxing', label: 'Boxing' },
];

const headerCls = "text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6] mb-3";

export default function TodaysSlate() {
  const [sportEvents, setSportEvents] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);

  useEffect(() => {
    setLoading(true);
    const todayStr = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local tz
    Promise.all(SPORT_KEYS.map(async (s) => {
      try {
        const r = await client.get(`/api/odds/${s.key}`);
        const todayEvents = (r.data || []).filter((ev) =>
          new Date(ev.commence_time).toLocaleDateString('en-CA') === todayStr
        );
        return { ...s, events: todayEvents };
      } catch { return { ...s, events: [] }; }
    })).then((results) => {
      const map = {};
      for (const r of results) { if (r.events.length > 0) map[r.key] = r; }
      setSportEvents(map);
      const keys = Object.keys(map);
      if (keys.length > 0) setActiveTab(keys[0]);
    }).finally(() => setLoading(false));
  }, []);

  const activeSports = Object.values(sportEvents);
  const selectedSport = activeTab ? sportEvents[activeTab] : null;

  if (loading) return (
    <div className="max-w-[1100px]">
      <h2 className={headerCls} style={{ fontFamily: 'var(--font-sans)' }}>Today's Slate</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">{[...Array(4)].map((_, i) => <div key={i} className="h-12 rounded skeleton" />)}</div>
    </div>
  );

  if (activeSports.length === 0) return (
    <div className="max-w-[1100px]">
      <h2 className={headerCls} style={{ fontFamily: 'var(--font-sans)' }}>Today's Slate</h2>
      <p className="text-[#6B7280] text-[14px]" style={{ fontFamily: 'var(--font-sans)' }}>No games today.</p>
    </div>
  );

  return (
    <div className="max-w-[1100px]">
      <h2 className={headerCls} style={{ fontFamily: 'var(--font-sans)' }}>Today's Slate</h2>

      {/* Sport tabs */}
      <div className="flex items-end gap-0 mb-4 border-b border-[#E5E7EB] dark:border-[#1e2536] pb-0">
        {activeSports.map((sport) => (
          <button key={sport.key} onClick={() => setActiveTab(sport.key)}
            className={`px-0 mr-6 pb-2 text-[14px] whitespace-nowrap transition-colors duration-150 border-b-2 ${
              activeTab === sport.key
                ? 'font-semibold text-[#111827] dark:text-[#e2e8f0] border-[#005BB3] dark:border-[#3b82f6]'
                : 'font-medium text-[#6B7280] dark:text-[#8892a8] border-transparent hover:text-[#374151] dark:hover:text-[#e2e8f0]'
            }`}
            style={{ fontFamily: 'var(--font-sans)' }}>
            {sport.label}
            <span className="text-[12px] text-[#9CA3AF] dark:text-[#505a6e] ml-1.5">{sport.events.length}</span>
          </button>
        ))}
      </div>

      {/* Game cards for selected sport */}
      {selectedSport && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
          {selectedSport.events.map((event) => <GameCard key={event.id} event={event} sportKey={selectedSport.key} />)}
        </div>
      )}
    </div>
  );
}

function GameCard({ event, sportKey }) {
  const time = new Date(event.commence_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  let spread = null;
  let favTeam = null;
  let favMl = null;
  for (const bm of event.bookmakers || []) {
    if (!spread) {
      const sp = bm.markets?.find((m) => m.key === 'spreads');
      if (sp) { const h = sp.outcomes.find((o) => o.name === event.home_team); if (h?.point != null) { const p = h.point; spread = p > 0 ? `+${p}` : `${p}`; } }
    }
    if (!favTeam) {
      const ml = bm.markets?.find((m) => m.key === 'h2h');
      if (ml && ml.outcomes.length >= 2) {
        const fav = ml.outcomes.reduce((a, b) => a.price < b.price ? a : b);
        favTeam = fav.name;
        favMl = formatPrice(fav.price);
      }
    }
    if ((spread || spread === null) && favTeam) break;
  }

  // Short team name: last word (e.g. "Los Angeles Lakers" → "Lakers")
  const shortFav = favTeam ? favTeam.split(' ').pop() : null;

  return (
    <Link to={`/game/${event.id}?sport=${sportKey}`} className="flex items-center justify-between rounded-lg border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-3 py-2 hover:bg-[#F8F9FA] dark:hover:bg-[#1a2030] transition-colors duration-150">
      <div className="min-w-0">
        <span className="text-[14px] font-medium text-[#111827] dark:text-[#e2e8f0] truncate block" style={{ fontFamily: 'var(--font-sans)' }}>{event.away_team || 'TBD'} <span className="text-[#6B7280] font-normal">@</span> {event.home_team || 'TBD'}</span>
        <span className="text-[12px] text-[#6B7280]" style={{ fontFamily: 'var(--font-sans)' }}>{time}</span>
      </div>
      <div className="flex flex-col items-end ml-2 shrink-0">
        {spread && <span className="text-[13px] text-[#1F2937] dark:text-[#8892a8] whitespace-nowrap" style={{ fontFamily: 'var(--font-mono)' }}>{spread}</span>}
        {shortFav && favMl && (
          <span className="text-[11px] text-[#6B7280] dark:text-[#505a6e] whitespace-nowrap" style={{ fontFamily: 'var(--font-mono)' }}>{shortFav} {favMl}</span>
        )}
      </div>
    </Link>
  );
}
